import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => { setIsMenuOpen(false); }, [location.pathname]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/services', label: 'Services' },
    { path: '/letting-agents', label: 'Letting Agents' },
    { path: '/property-management', label: 'Property Management' },
    { path: '/process', label: 'How It Works' },
    { path: '/testimonials', label: 'Reviews' },
    { path: '/blog', label: 'Blog' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top utility bar (dark) */}
      <div style={{
        background: 'var(--royal-dark-deepest)',
        color: 'var(--text-on-dark-dimmed)',
        padding: '10px 0',
        fontSize: '0.825rem',
        borderBottom: '1px solid rgba(212, 175, 55, 0.15)',
        letterSpacing: '0.02em'
      }}>
        <div className="royal-container" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '28px', flexWrap: 'wrap' }}>
            <a href="tel:07710613783" style={{
              color: 'var(--royal-gold-bright)',
              textDecoration: 'none',
              display: 'flex', alignItems: 'center', gap: '8px',
              fontWeight: '600'
            }} data-testid="topbar-phone">
              <Phone size={14} />
              <span>07710 613783</span>
            </a>
            <a href="mailto:Joe@RoyalSecurity.info" style={{
              color: 'var(--text-on-dark-muted)',
              textDecoration: 'none',
              display: 'flex', alignItems: 'center', gap: '8px'
            }} data-testid="topbar-email">
              <Mail size={14} />
              <span>Joe@RoyalSecurity.info</span>
            </a>
          </div>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '3px 12px',
            background: 'rgba(191, 10, 48, 0.15)',
            border: '1px solid rgba(191, 10, 48, 0.4)',
            borderRadius: '20px',
            color: '#FF6B81',
            fontWeight: '600',
            fontSize: '0.75rem',
            letterSpacing: '0.05em',
            textTransform: 'uppercase'
          }} data-testid="topbar-emergency-badge">
            <span style={{
              width: '6px', height: '6px', borderRadius: '50%',
              background: '#FF6B81', boxShadow: '0 0 8px #FF6B81',
              animation: 'rsPulse 1.6s ease-in-out infinite'
            }} />
            24/7 Emergency Call-Out
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header style={{
        background: scrolled ? 'rgba(18, 18, 18, 0.92)' : 'var(--royal-dark-charcoal)',
        backdropFilter: scrolled ? 'blur(16px) saturate(140%)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(16px) saturate(140%)' : 'none',
        borderBottom: '1px solid rgba(212, 175, 55, 0.25)',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        transition: 'background 0.3s ease',
        boxShadow: scrolled ? '0 4px 20px rgba(0, 0, 0, 0.4)' : 'none'
      }} data-testid="site-header">
        <nav className="royal-container header-nav" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 24px',
          minHeight: '72px'
        }}>
          {/* Logo */}
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', flexShrink: 0, marginRight: '24px' }} data-testid="header-logo-link">
            <img
              src="/brand/royal-logo-wordmark.png"
              alt="Royal Security"
              className="header-logo"
              style={{ height: '56px', width: 'auto', display: 'block' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div style={{
            display: 'flex',
            gap: '28px',
            alignItems: 'center'
          }} className="desktop-nav">
            {navLinks.map((link) => {
              const active = isActive(link.path);
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  style={{
                    textDecoration: 'none',
                    color: active ? 'var(--royal-gold-bright)' : '#E5E5E5',
                    fontWeight: active ? '700' : '500',
                    fontSize: '0.9rem',
                    letterSpacing: '0.03em',
                    transition: 'color 0.25s ease, border-color 0.25s ease',
                    borderBottom: active ? '2px solid var(--royal-gold-bright)' : '2px solid transparent',
                    paddingBottom: '4px'
                  }}
                  onMouseEnter={(e) => { if (!active) e.target.style.color = 'var(--royal-gold-bright)'; }}
                  onMouseLeave={(e) => { if (!active) e.target.style.color = '#E5E5E5'; }}
                >
                  {link.label}
                </Link>
              );
            })}
            <Link
              to="/contact"
              className="royal-btn-primary"
              style={{ marginLeft: '8px', padding: '11px 22px', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
              data-testid="header-get-quote-button"
            >
              Get a Quote
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            style={{
              display: 'none',
              background: 'rgba(212, 175, 55, 0.1)',
              border: '1px solid var(--royal-gold-border)',
              borderRadius: '6px',
              cursor: 'pointer',
              color: 'var(--royal-gold-bright)',
              padding: '8px'
            }}
            className="mobile-menu-btn"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div style={{
            background: 'var(--royal-dark-charcoal)',
            borderTop: '1px solid rgba(212, 175, 55, 0.2)',
            padding: '20px 24px'
          }} className="mobile-menu" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                style={{
                  display: 'block',
                  padding: '14px 0',
                  textDecoration: 'none',
                  color: isActive(link.path) ? 'var(--royal-gold-bright)' : '#E5E5E5',
                  fontWeight: isActive(link.path) ? '700' : '500',
                  fontSize: '0.95rem',
                  letterSpacing: '0.03em',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.06)'
                }}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className="royal-btn-primary"
              onClick={() => setIsMenuOpen(false)}
              style={{ marginTop: '20px', width: '100%', justifyContent: 'center' }}
              data-testid="mobile-get-quote-button"
            >
              Get a Quote
            </Link>
          </div>
        )}
      </header>
    </>
  );
};
